import { useState, useCallback } from 'react';
import { useScanJob } from '../hooks/useScanJob';
import { Progress } from '../components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Camera, Upload, CheckCircle2, XCircle, Loader2, User, Hash, BookOpen, RotateCcw, ListChecks, FileText, Trash2, Plus, ScanLine } from 'lucide-react';
import { useDropzone } from 'react-dropzone';

import { usePageTitle } from '../hooks/usePageTitle';

export default function Scanner() {
  usePageTitle('Scanner');
  const [files, setFiles] = useState([]);
  const [batchResults, setBatchResults] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);

  const onJobDone = useCallback((data) => {
    setBatchResults(data);
    const successes = data.results.filter(r => !r.error);
    setScanHistory(prev => [...successes, ...prev]);
    if (successes.length > 0) toast.success(`${successes.length} copie(s) corrigée(s) sur ${data.total_pages} page(s)`);
    const errors = data.results.filter(r => r.error);
    if (errors.length > 0) toast.error(`${errors.length} erreur(s) détectée(s)`);
  }, []);
  const onJobError = useCallback((msg) => toast.error(msg), []);
  const { start: startScan, cancel: cancelScan, progress, jobId, busy: scanning } = useScanJob({ onDone: onJobDone, onError: onJobError });

  const onDrop = useCallback((accepted) => {
    setFiles(prev => [...prev, ...accepted]);
    setBatchResults(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.heic'],
      'application/pdf': ['.pdf']
    },
    multiple: true,
    onDrop
  });

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const handleScanBatch = async () => {
    if (files.length === 0) { toast.error('Ajoutez au moins un fichier'); return; }
    setBatchResults(null);
    try { await startScan(files); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'envoi des fichiers"); }
  };

  const resetScan = () => {
    setFiles([]);
    setBatchResults(null);
  };

  const gradeColor = (note, bareme = 20) => {
    const pct = (note / bareme) * 20;
    if (pct >= 16) return 'text-emerald-400';
    if (pct >= 12) return 'text-blue-400';
    if (pct >= 10) return 'text-amber-400';
    return 'text-red-400';
  };

  const gradeBg = (note, bareme = 20) => {
    const pct = (note / bareme) * 20;
    if (pct >= 16) return 'bg-emerald-400/10 border-emerald-400/30';
    if (pct >= 12) return 'bg-blue-400/10 border-blue-400/30';
    if (pct >= 10) return 'bg-amber-400/10 border-amber-400/30';
    return 'bg-red-400/10 border-red-400/30';
  };

  const fileIcon = (f) => f.type === 'application/pdf' ? <FileText className="w-4 h-4 text-red-400" /> : <Camera className="w-4 h-4 text-blue-400" />;

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl mx-auto" data-testid="scanner-page">
      <div>
        <h1 className="text-3xl font-black tracking-tight">Scanner des copies</h1>
        <p className="text-muted-foreground mt-1">
          Importez les copies scannées (images ou PDF). Les pages multi-élèves sont regroupées automatiquement par ID.
        </p>
      </div>

      <Card className="bg-card border-border border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-start gap-3 text-sm">
            <div className="flex items-center gap-6 flex-wrap">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Hash className="w-4 h-4 text-primary" /> ID détecté par page
              </span>
              <span className="text-muted-foreground">→</span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <User className="w-4 h-4 text-blue-400" /> Pages regroupées par élève
              </span>
              <span className="text-muted-foreground">→</span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <BookOpen className="w-4 h-4 text-emerald-400" /> Notes calculées
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* File Upload Zone */}
      <Card className="bg-card border-border">
        <CardContent className="p-5 space-y-4">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
            data-testid="scan-dropzone"
          >
            <input {...getInputProps()} />
            <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Glissez-déposez vos fichiers ou cliquez pour sélectionner</p>
            <p className="text-xs text-muted-foreground mt-1">Images (JPG, PNG) ou PDF — Sélection multiple autorisée</p>
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div className="space-y-2" data-testid="file-list">
              {files.map((f, i) => (
                <div key={`${f.name}-${f.size}`} className="flex items-center gap-3 p-2 rounded-lg bg-secondary/50" data-testid={`file-${i}`}>
                  {fileIcon(f)}
                  <span className="text-sm flex-1 truncate">{f.name}</span>
                  <span className="text-xs text-muted-foreground">{(f.size / 1024).toFixed(0)} Ko</span>
                  <Button variant="ghost" size="sm" onClick={() => removeFile(i)} className="text-muted-foreground hover:text-destructive shrink-0">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => document.querySelector('[data-testid="scan-dropzone"] input')?.click()}
                className="text-xs text-primary hover:underline flex items-center gap-1"
                data-testid="add-more-files"
              >
                <Plus className="w-3 h-3" /> Ajouter d'autres fichiers
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scan Button */}
      {files.length > 0 && !batchResults && (
        <Button
          data-testid="scan-submit-btn"
          onClick={handleScanBatch}
          disabled={scanning}
          className="w-full bg-primary text-primary-foreground hover:bg-blue-600 h-12 text-base"
        >
          {scanning ? (
            <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Analyse en cours...</>
          ) : (
            <><Camera className="w-5 h-5 mr-2" /> Analyser {files.length} fichier{files.length > 1 ? 's' : ''}</>
          )}
        </Button>
      )}

      {/* Progress (upload → OCR → grading) */}
      {progress && (
        <Card className="border border-primary/30 bg-primary/5 animate-fade-in" data-testid="scan-progress">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 font-medium">
                {progress.phase === 'upload' ? <Upload className="w-4 h-4 text-primary" /> : <ScanLine className="w-4 h-4 text-primary animate-pulse" />}
                <span data-testid="scan-progress-label">{progress.label}</span>
              </span>
              <span className="font-bold text-primary" data-testid="scan-progress-percent">{progress.percent} %</span>
            </div>
            <Progress value={progress.percent} className="h-2" />
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground truncate" data-testid="scan-progress-detail">
                {progress.resumed > 0 && <span className="text-amber-400 mr-2">Reprise automatique après redémarrage du serveur ·</span>}
                {progress.detail || (progress.phase === 'ocr' ? 'Vous pouvez quitter cette page : le traitement continue en arrière-plan.' : '')}
              </p>
              {jobId && (
                <Button variant="ghost" size="sm" onClick={cancelScan} className="h-7 text-xs text-muted-foreground hover:text-destructive shrink-0" data-testid="scan-cancel-btn">
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Annuler
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Batch Results */}
      {batchResults && (
        <Card className="border border-border animate-slide-up" data-testid="batch-results">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ListChecks className="w-5 h-5 text-primary" />
              Résultats — {batchResults.total_pages} page(s) analysée(s), {batchResults.grouped_copies || batchResults.results?.length} copie(s) détectée(s)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {batchResults.results.map((r, i) => (
              <div key={r.exam_id || `result-${i}`} className={`p-3 rounded-lg border ${r.error ? 'bg-red-400/5 border-red-400/20' : gradeBg(r.note, r.bareme)}`} data-testid={`batch-result-${i}`}>
                {r.error ? (
                  <div className="flex items-center gap-3">
                    <XCircle className="w-5 h-5 text-red-400 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">ID: {r.exam_id}</p>
                      <p className="text-xs text-red-400">{r.error}</p>
                      {r.student_name && <p className="text-xs text-muted-foreground">{r.student_name} — {r.note}/{r.bareme}</p>}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                      <div>
                        <p className="font-medium text-sm">{r.student_name}</p>
                        <p className="text-xs text-muted-foreground">ID: {r.exam_id} — {r.classe} — {r.total_correct}/{r.total_questions} correct{r.pages_scanned > 1 ? ` — ${r.pages_scanned}/${r.total_pages} pages` : ''}</p>
                      </div>
                    </div>
                    <span className={`text-2xl font-black ${gradeColor(r.note, r.bareme)}`}>{r.note}/{r.bareme}</span>
                  </div>
                )}
              </div>
            ))}
            {batchResults.unmatched_pages > 0 && (
              <p className="text-xs text-amber-400">
                {batchResults.unmatched_pages} page(s) sans ID détecté (ignorées)
              </p>
            )}
            <Button data-testid="scan-reset-btn" onClick={resetScan} className="w-full bg-primary text-primary-foreground hover:bg-blue-600 mt-2">
              <RotateCcw className="w-4 h-4 mr-2" /> Scanner d'autres copies
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Scan History */}
      {scanHistory.length > 0 && !batchResults && (
        <Card className="bg-card border-border" data-testid="scan-history">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ListChecks className="w-5 h-5 text-primary" /> Copies scannées ({scanHistory.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {scanHistory.map((r, i) => (
                <div key={r.exam_id || `hist-${i}`} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30" data-testid={`history-item-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-muted-foreground">#{r.exam_id}</span>
                    <span className="font-medium text-sm">{r.student_name}</span>
                    <span className="text-xs text-muted-foreground">{r.classe}</span>
                  </div>
                  <span className={`text-lg font-black ${gradeColor(r.note, r.bareme)}`}>{r.note}/{r.bareme}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
